import { useEffect, useState } from 'react';
import { Gem, Loader2, LogOut, Settings2, Sparkles } from 'lucide-react';
import { checkin, fetchBootstrap } from '../api/playground';
import { clearAuthSession } from '../services/auth';
import { toast } from '../utils/toast';
import ToastHost from '../components/ui/Toast';
import ThemeToggle from '../components/ui/ThemeToggle';
import type { BootstrapResponse, DailyQuota, PlaygroundAgent } from '../types';
import AgentListPanel from '../components/workbench/AgentListPanel';
import ChatPanel from '../components/workbench/ChatPanel';
import ConfigPanel from '../components/workbench/ConfigPanel';

/**
 * 工作台 —— 三栏布局壳（练玉坊主体）。
 *
 * 数据流约定：挂载时调用 GET /playground/bootstrap 拉取
 * { user, tenants, agents, usage }，持有 bootstrap / loading / error / selectedAgentId
 * 四态向下分发；各栏组件不自己拉 bootstrap。
 * - 左：Agent 列表（AgentListPanel，内部轮询运行时状态 + 启停 + 新建 + 同步到应龙）
 * - 中：对话区（ChatPanel）
 * - 右：配置面板（ConfigPanel，五维配置）
 * - 顶栏：产品名 / 当月成本 + 选中 Agent 预算进度条 / 今日平台额度（token + 时长双徽标）
 *         + 未签到时的「签到领取今日额度」按钮 / 用户菜单（登出）；窄屏时右栏抽屉化
 */

/** token 数紧凑显示：1200 → 1.2k，20000 → 20k */
function fmtTokens(n: number): string {
  if (n >= 1000) {
    const k = n / 1000;
    return `${Number.isInteger(k) ? k : k.toFixed(1)}k`;
  }
  return String(n);
}

/** 用量占比 >80% 时徽标警示色 */
function quotaWarn(used: number, grant: number): boolean {
  return grant > 0 && used / grant > 0.8;
}

export default function Workbench() {
  const [bootstrap, setBootstrap] = useState<BootstrapResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedAgentId, setSelectedAgentId] = useState<string | null>(null);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [checkingIn, setCheckingIn] = useState(false);

  useEffect(() => {
    let cancelled = false;
    fetchBootstrap()
      .then((data) => {
        if (cancelled) return;
        setBootstrap(data);
        setSelectedAgentId((prev) => prev ?? data.agents[0]?.id ?? null);
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        const e = err as { response?: { data?: { detail?: unknown } }; message?: unknown };
        const detail = e.response?.data?.detail;
        setError(
          typeof detail === 'string'
            ? detail
            : typeof e.message === 'string'
              ? e.message
              : '工作台初始化失败'
        );
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const agents = bootstrap?.agents ?? [];
  const user = bootstrap?.user;
  const monthUsage = bootstrap?.usage?.month;
  const daily: DailyQuota | null = bootstrap?.usage?.daily ?? null;
  const selectedAgent = agents.find((a) => a.id === selectedAgentId) ?? null;

  const handleLogout = () => {
    clearAuthSession();
    window.location.href = '/login';
  };

  const handleAgentCreated = (agent: PlaygroundAgent) => {
    setBootstrap((prev) => (prev ? { ...prev, agents: [agent, ...prev.agents] } : prev));
    setSelectedAgentId(agent.id);
  };

  /** 刷新 bootstrap（发布后调用）：保留当前选中（仍存在时），否则回退第一个 */
  const refreshBootstrap = async () => {
    try {
      const data = await fetchBootstrap();
      setBootstrap(data);
      setSelectedAgentId((prev) => (prev && data.agents.some((a) => a.id === prev) ? prev : (data.agents[0]?.id ?? null)));
    } catch {
      // 刷新失败不打断当前界面，下次操作再试
    }
  };

  /** 签到：领取今日 token 与时长额度（幂等），成功后刷新 bootstrap 收敛徽标 */
  const handleCheckin = async () => {
    if (checkingIn) return;
    setCheckingIn(true);
    try {
      const res = await checkin();
      toast(res.already ? '今日已签到' : '签到成功，已领取今日 token 与时长额度', 'success');
      await refreshBootstrap();
    } catch (err) {
      const e = err as { response?: { data?: { detail?: unknown } } };
      const detail = e.response?.data?.detail;
      toast(typeof detail === 'string' ? detail : '签到失败，请稍后重试', 'error');
    } finally {
      setCheckingIn(false);
    }
  };

  /** 模型 Tab 保存预算后：本地更新选中 Agent 的 budget，顶栏进度条即时反映 */
  const handleBudgetChanged = (agentId: string, cap: number | null) => {
    setBootstrap((prev) =>
      prev
        ? {
            ...prev,
            agents: prev.agents.map((a) =>
              a.id === agentId ? { ...a, budget: cap === null ? null : { monthly_cost_cap: cap } } : a
            ),
          }
        : prev
    );
  };

  // 顶栏进度条：优先用选中 Agent 的月度成本上限（>80% 警示 / ≥100% 危险色）
  const cap =
    typeof selectedAgent?.budget?.monthly_cost_cap === 'number' && selectedAgent.budget.monthly_cost_cap > 0
      ? selectedAgent.budget.monthly_cost_cap
      : null;
  const usageRatio = cap && monthUsage ? Math.min(monthUsage.cost / cap, 1) : null;

  return (
    <div className="pg-shell">
      <header className="pg-header">
        <div className="pg-brand">
          <div className="pg-brand-mark">
            <Gem size={16} />
          </div>
          练玉坊
          <span className="pg-brand-sub">Agent 练习工作台</span>
        </div>
        <div className="pg-header-right">
          {monthUsage && (
            <span
              className="usage-badge"
              title={
                cap !== null
                  ? `本月 ${monthUsage.calls} 次调用 / ${monthUsage.total_tokens} tokens；选中 Agent 预算上限 ¥${cap}`
                  : `本月 ${monthUsage.calls} 次调用 / ${monthUsage.total_tokens} tokens`
              }
            >
              <span className="usage-badge__text">
                本月 ¥{monthUsage.cost.toFixed(2)} · {monthUsage.calls} 次调用
                {cap !== null ? ` / 预算 ¥${cap.toFixed(2)}` : ''}
              </span>
              {usageRatio !== null && (
                <span className="usage-badge__bar">
                  <span
                    className={`usage-badge__fill${usageRatio >= 1 ? ' usage-badge__fill--full' : usageRatio >= 0.8 ? ' usage-badge__fill--warn' : ''}`}
                    style={{ width: `${Math.max(usageRatio * 100, 2)}%` }}
                  />
                </span>
              )}
            </span>
          )}
          {daily && (
            <>
              <span
                className={`usage-badge usage-badge--compact${quotaWarn(daily.tokens.used, daily.tokens.grant) ? ' usage-badge--warn' : ''}`}
                title={`今日平台 token 额度：已用 ${daily.tokens.used}/${daily.tokens.grant}，明日重置；配置自己的模型 Key 即可不限量`}
              >
                token {fmtTokens(daily.tokens.used)}/{fmtTokens(daily.tokens.grant)}
              </span>
              <span
                className={`usage-badge usage-badge--compact${quotaWarn(daily.minutes.used, daily.minutes.grant) ? ' usage-badge--warn' : ''}`}
                title={`今日平台时长额度：已用 ${daily.minutes.used}/${daily.minutes.grant} 分钟，明日重置`}
              >
                时长 {daily.minutes.used}/{daily.minutes.grant}分
              </span>
              {!daily.checked_in && (
                <button
                  type="button"
                  className="btn pg-checkin-btn"
                  disabled={checkingIn}
                  onClick={() => void handleCheckin()}
                  title="签到领取今日平台 token 与时长额度"
                >
                  {checkingIn ? <Loader2 size={13} className="spin" /> : <Sparkles size={13} />}
                  {checkingIn ? '签到中…' : '签到领取今日额度'}
                </button>
              )}
            </>
          )}
          {user && <span className="user-chip">{user.display_name || user.email}</span>}
          <ThemeToggle />
          <button
            className="btn btn-icon pg-config-toggle"
            title={drawerOpen ? '收起配置面板' : '打开配置面板'}
            onClick={() => setDrawerOpen((v) => !v)}
          >
            <Settings2 size={16} />
          </button>
          <button className="btn btn-icon" title="退出登录" onClick={handleLogout}>
            <LogOut size={16} />
          </button>
        </div>
      </header>

      <main className="pg-main">
        <aside className="pg-panel-left">
          <AgentListPanel
            agents={agents}
            loading={loading}
            error={error}
            selectedAgentId={selectedAgentId}
            onSelectAgent={setSelectedAgentId}
            onAgentCreated={handleAgentCreated}
            onAgentDeleted={(agentId) => setSelectedAgentId((current) => (current === agentId ? null : current))}
            onRefresh={() => void refreshBootstrap()}
          />
        </aside>

        <section className="pg-panel-center">
          {loading ? (
            <div className="pg-state">
              <Loader2 size={22} className="spin" />
              加载工作台...
            </div>
          ) : (
            <ChatPanel agent={selectedAgent} />
          )}
        </section>

        {drawerOpen && <div className="pg-drawer-backdrop" onClick={() => setDrawerOpen(false)} />}
        <aside className={`pg-panel-right${drawerOpen ? ' pg-panel-right--open' : ''}`}>
          {/* key=agent.id：切换 Agent 时右栏整体重建（Tab 回到「角色」、数据重新加载） */}
          <ConfigPanel
            key={selectedAgent?.id ?? 'none'}
            agent={selectedAgent}
            daily={daily}
            onBudgetChanged={handleBudgetChanged}
          />
        </aside>
      </main>

      <ToastHost />
    </div>
  );
}
